import {
  Children,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/cn';
import { remPx } from '../../lib/rem';

/**
 * The app's dropdown. Looks the same in every browser.
 *
 * A native `<select>` renders its option list with the OPERATING SYSTEM's widget — Chrome on
 * Windows draws a flat grey list, macOS a dark rounded sheet — so those fields never matched the
 * designed dropdowns elsewhere in the app and couldn't be styled into matching. This draws the list
 * itself while keeping a real `<select>` in the DOM for form semantics.
 *
 * Deliberately a DROP-IN for the old component: same `<option>` children, same `value`/`onChange`
 * contract (handlers still read `e.target.value`, and `e.target` is a real `HTMLSelectElement`).
 * That is what let every existing call site stay untouched.
 */

interface ParsedOption {
  value: string;
  label: ReactNode;
  /** Plain text of the label — used for the closed trigger and type-ahead. */
  text: string;
  disabled: boolean;
  /** Tooltip for the row — typically why a disabled option is unavailable. */
  title?: string;
}

/** Best-effort plain text for a node, so type-ahead and the trigger work with formatted labels. */
function nodeText(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join('');
  if (isValidElement(node)) return nodeText((node.props as { children?: ReactNode }).children);
  return '';
}

/**
 * Read `<option>` children into a list we can render ourselves. Conditionals (`cond && <option/>`),
 * fragments and arrays all flatten, because call sites build their options that way.
 */
function parseOptions(children: ReactNode): ParsedOption[] {
  const out: ParsedOption[] = [];
  const visit = (nodes: ReactNode): void => {
    Children.forEach(nodes, (child) => {
      if (!isValidElement(child)) return;
      if (child.type === 'option') {
        const props = child.props as {
          value?: string | number | readonly string[];
          children?: ReactNode;
          disabled?: boolean;
          label?: string;
          title?: string;
        };
        const label = props.children ?? props.label ?? '';
        const value = props.value === undefined ? nodeText(label) : String(props.value);
        out.push({
          value,
          label,
          text: props.label ?? nodeText(label),
          disabled: Boolean(props.disabled),
          title: props.title,
        });
        return;
      }
      // Fragments and wrapper elements — look inside rather than dropping their options.
      const nested = (child.props as { children?: ReactNode }).children;
      if (nested !== undefined) visit(nested);
    });
  };
  visit(children);
  return out;
}

type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> & {
  /** Text shown when the current value matches no option and there is no empty-valued option. */
  placeholder?: string;
  /** Fired when the list opens — e.g. to close another overlay that would sit on top of it. */
  onOpen?: () => void;
  /**
   * Put a search box in the open list and filter as you type.
   *
   * Opt-in, and off for every existing call site: a dozen options need scrolling, not searching.
   * It exists for the font picker, where the list is ~1900 rows and scrolling is not a way to find
   * anything.
   */
  searchable?: boolean;
  /** Placeholder for that search box. */
  searchPlaceholder?: string;
  /**
   * Let the user keep text that matches no option, offered as a "Use …" row.
   *
   * For the font field: a licensed brand font will never be in the Google catalog and must still be
   * nameable, which is why that field was free text in the first place.
   */
  allowCustomValue?: boolean;
};

/**
 * Route passthrough props to the element they actually describe.
 *
 * Everything used to land on the hidden `<select>`, which carries `aria-hidden` — so an
 * `aria-label` (the ONLY label three call sites have) was dropped from the accessibility tree
 * entirely and those comboboxes announced their value instead of their name.
 */
function splitProps(rest: Record<string, unknown>): {
  forButton: Record<string, unknown>;
  forNative: Record<string, unknown>;
} {
  const forButton: Record<string, unknown> = {};
  const forNative: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(rest)) {
    if (k.startsWith('aria-') || k.startsWith('data-') || k === 'title' || k === 'autoFocus') {
      forButton[k] = v;
    } else {
      // name / required / form / autoComplete stay on the real control that submits.
      forNative[k] = v;
    }
  }
  return { forButton, forNative };
}

/** Does a caller-supplied className already set a width? Then don't force `w-full` over it. */
const SETS_WIDTH = /(^|\s)(w-\d|w-\[|w-full|w-auto|w-fit|w-px|w-1\/|w-2\/|w-3\/|w-4\/|w-5\/|w-6\/|w-min|w-max|w-screen)/;

/**
 * The list's sizes, in rem like the rows they hold, so the list keeps its proportions when index.css
 * scales the UI to 80%. `place()` works in measured px, so it multiplies these by `remPx()`; the
 * comments give each at full size.
 */
const MENU_MAX_HEIGHT_REM = 18; // 288px — about 8 rows before the list scrolls.
const MENU_MIN_HEIGHT_REM = 6; // 96px — the least room it is squeezed into near a screen edge.

/**
 * How wide the list may grow, independent of the field it belongs to: 300px at full size.
 *
 * The list used to be exactly the trigger's width, so a narrow field truncated its own options —
 * "Vertical / S…", "Link Previe…" — and the only way to read one was to pick it. A dropdown is an
 * overlay: it can be wider than the control that opened it. Past this it wraps instead of growing,
 * because a list wide enough for the longest label is its own readability problem.
 */
const MENU_MAX_WIDTH_REM = 18.75;
const VIEWPORT_MARGIN_REM = 0.5; // 8px kept clear at the window's edges.
const MENU_GAP_REM = 0.25; // 4px between the field and its list.

/**
 * Most rows drawn at once while searching.
 *
 * Only ~8 fit in the 18rem box, so this is not about what can be seen — it is about not building
 * two thousand <li> elements (each with its own handlers) on the first keystroke. Anything past it
 * is reachable by typing one more character, and the list says so.
 */
const MAX_VISIBLE_ROWS = 200;

export function Select({
  className,
  children,
  value,
  defaultValue,
  onChange,
  disabled,
  id,
  placeholder,
  onOpen,
  searchable = false,
  searchPlaceholder = 'Search…',
  allowCustomValue = false,
  ...rest
}: SelectProps) {
  const options = useMemo(() => parseOptions(children), [children]);
  const nativeRef = useRef<HTMLSelectElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  // The whole floating panel, which is MORE than the list: when searching, the search box and its
  // padding are siblings of the <ul>. The dismiss handler has to know they are part of the widget.
  const panelRef = useRef<HTMLDivElement>(null);
  const reactId = useId();
  const listId = `${id ?? reactId}-listbox`;

  // Uncontrolled fallback: only consulted when `value` is not supplied.
  const [internal, setInternal] = useState<string>(() =>
    defaultValue === undefined ? '' : String(defaultValue),
  );
  const current = value === undefined ? internal : String(value);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [pos, setPos] = useState<{
    left?: number;
    right?: number;
    top?: number;
    bottom?: number;
    minWidth: number;
    maxWidth: number;
    maxHeight: number;
  } | null>(null);

  // Where the list is portalled. document.body puts it OUTSIDE any aria-modal dialog, and content
  // outside an aria-modal subtree is exactly what assistive tech is told to ignore — so the options
  // were unreachable by screen readers inside every modal. Portal into the dialog when there is
  // one; position:fixed still resolves against the viewport (the panel sets no transform, and
  // overflow:hidden does not clip fixed descendants).
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (!open) return;
    const dialog = triggerRef.current?.closest<HTMLElement>('[role="dialog"]');
    setPortalTarget(dialog ?? document.body);
  }, [open]);

  const { forButton, forNative } = useMemo(() => splitProps(rest), [rest]);

  const selectedIndex = options.findIndex((o) => o.value === current);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null;

  /**
   * The rows actually drawn — and the ONLY thing `activeIndex` indexes.
   *
   * Everything positional (the highlight, arrow stepping, `aria-activedescendant`, the Enter
   * commit) reads this rather than `options`, because with a filter applied the two no longer line
   * up and a stale index would commit whatever happened to sit at that position.
   */
  const { rows, hiddenMore } = useMemo(() => {
    const q = searchable ? query.trim().toLowerCase() : '';
    const matched = q ? options.filter((o) => o.text.toLowerCase().includes(q)) : options;
    // Only a searchable list is capped. Applying it unconditionally silently made option 201
    // unselectable in every OTHER dropdown in the app — none of which have a search box to reach
    // the rest with.
    const capped = searchable ? matched.slice(0, MAX_VISIBLE_ROWS) : matched;
    const typed = query.trim();
    // A custom row only when the text is genuinely new — offering `Use "Inter"` next to Inter is
    // just a second way to pick the same thing.
    const isNew =
      allowCustomValue &&
      typed.length > 0 &&
      !options.some((o) => o.text.toLowerCase() === typed.toLowerCase());
    return {
      // LAST, not first. As the leading row it was also the row the highlight opened on, so typing
      // "montse" and pressing Enter — the most natural way to use this — saved the fragment
      // "montse" instead of Montserrat. Behind the matches, Enter takes the obvious match, and a
      // name that matches nothing still leaves the custom row as the only thing to pick.
      rows: isNew
        ? [...capped, { value: typed, label: `Use “${typed}”`, text: typed, disabled: false }]
        : capped,
      // Counted from MATCHED, before the cap — measuring the capped list against the cap is
      // always zero, which is precisely when the "keep typing" hint needs to appear.
      hiddenMore: searchable ? Math.max(0, matched.length - capped.length) : 0,
    };
  }, [allowCustomValue, options, query, searchable]);

  /** Anchor the list to the trigger in VIEWPORT coordinates. */
  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const rem = remPx();
    const margin = VIEWPORT_MARGIN_REM * rem;
    const gap = MENU_GAP_REM * rem;
    const menuMaxHeight = MENU_MAX_HEIGHT_REM * rem;
    const below = window.innerHeight - r.bottom - margin;
    const above = r.top - margin;
    // Prefer dropping down; flip up only when below is genuinely tighter.
    const useAbove = below < Math.min(menuMaxHeight, above) && above > below;
    const maxHeight = Math.min(
      menuMaxHeight,
      Math.max(MENU_MIN_HEIGHT_REM * rem, useAbove ? above : below),
    );
    // Never narrower than the field (a list that doesn't reach the control's edges reads as
    // broken), never wider than the cap — EXCEPT for a field already wider than the cap, where
    // matching it is what looks right.
    const maxWidth = Math.max(MENU_MAX_WIDTH_REM * rem, r.width);
    // Growing rightward from the field's left edge is the normal case. When the list COULD outgrow
    // the space there, anchor its RIGHT edge to the field's instead and let it grow leftward —
    // clamping `left` by the widest possible panel would slide the list bodily away from the field
    // it belongs to (measured: 140px adrift on a field near the right edge).
    const anchorRight = r.left + maxWidth > window.innerWidth - margin;
    setPos({
      left: anchorRight ? undefined : Math.max(margin, r.left),
      right: anchorRight ? Math.max(margin, window.innerWidth - r.right) : undefined,
      // Flipping up anchors the list's BOTTOM to the trigger. Using `top: trigger.top - maxHeight`
      // positioned it by the height CAP instead of its real height, so a two-item list floated far
      // above the field with a gap. `bottom` needs no height measurement to sit flush.
      top: useAbove ? undefined : r.bottom + gap,
      bottom: useAbove ? window.innerHeight - r.top + gap : undefined,
      minWidth: r.width,
      maxWidth,
      maxHeight,
    });
  }, []);

  // position:fixed is measured from the viewport, so ANY ancestor scrolling moves the trigger out
  // from under the list. Recompute on scroll anywhere (capture catches nested scrollers — these
  // fields live inside scrolling modals) and on resize.
  useLayoutEffect(() => {
    if (!open) return;
    place();
    const onScroll = () => place();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, place]);

  // Dismiss on an outside press or Escape — WITHOUT the surrounding dialog also reacting.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as Node;
      // panelRef as well as listRef: `listRef` is on the <ul>, so with a search box open a click
      // straight into that box read as an outside press and closed the picker before a
      // caret could land. Anything inside the panel is inside the widget.
      if (
        triggerRef.current?.contains(t) ||
        listRef.current?.contains(t) ||
        panelRef.current?.contains(t)
      ) {
        return;
      }
      setOpen(false);
      // Closing on pointerdown leaves the matching `click` to land on whatever was underneath —
      // and in a dialog that is usually the backdrop button, so dismissing the list closed the
      // whole modal. Swallow exactly one following click, the way a native option popup does.
      const swallow = (ev: MouseEvent) => {
        ev.preventDefault();
        ev.stopPropagation();
      };
      window.addEventListener('click', swallow, { capture: true, once: true });
      // If no click follows (a drag, a right-press), don't leave the trap armed.
      window.setTimeout(() => window.removeEventListener('click', swallow, true), 300);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    // On WINDOW, not document. Modal registers its own Escape handler on `document` in the capture
    // phase, and stopPropagation() does not stop a listener on the SAME node in the SAME phase —
    // worse, Modal's is registered when the dialog opens, so it runs first and closed the whole
    // dialog. Capture starts at window, one step earlier in the path, so stopping here means
    // document never sees the key at all.
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  // Focus the search box from ITS OWN effect, not from openMenu: the portal only mounts once
  // `pos` and `portalTarget` are set by the layout effects above, so at openMenu time the input
  // does not exist yet and the focus call would land on nothing.
  useEffect(() => {
    if (open && searchable && pos && portalTarget) searchRef.current?.focus();
  }, [open, pos, portalTarget, searchable]);

  // A stale query would filter the list before the user has typed anything the next time it opens.
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  // Keep the highlighted row visible while arrowing.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    listRef.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)?.scrollIntoView({
      block: 'nearest',
    });
  }, [open, activeIndex]);

  const commit = useCallback(
    (next: string) => {
      // A native select fires no change event when the user re-picks the current option.
      if (next === current) return;
      if (value === undefined) setInternal(next);
      const el = nativeRef.current;
      if (el) {
        // Set the real element FIRST so a handler reading `e.target.value` sees the new value.
        el.value = next;
        onChange?.({
          target: el,
          currentTarget: el,
        } as unknown as ChangeEvent<HTMLSelectElement>);
      }
    },
    [current, onChange, value],
  );

  const openMenu = useCallback(
    (startAt?: number) => {
      if (disabled) return;
      const from = startAt ?? rows.findIndex((o) => (current ? o.value === current : !o.disabled));
      setActiveIndex(from >= 0 ? from : rows.findIndex((o) => !o.disabled));
      setOpen(true);
      onOpen?.();
      // Safari and Firefox don't focus a button on mouse-press, so a list opened by clicking had
      // no keyboard target and arrow keys did nothing.
      triggerRef.current?.focus();
    },
    [current, disabled, onOpen, rows],
  );

  /** Move the highlight, skipping disabled rows; stops at the ends rather than wrapping. */
  const step = useCallback(
    (from: number, delta: number) => {
      let i = from;
      for (let guard = 0; guard < rows.length; guard += 1) {
        i += delta;
        if (i < 0 || i >= rows.length) return from < 0 ? -1 : from;
        if (!rows[i].disabled) return i;
      }
      return from;
    },
    [rows],
  );

  // Filtering changes what row 0 IS, so a highlight left at an old position points at the wrong
  // font. Snap back to the top of the results when the QUERY changes — and only then.
  //
  // Keyed off `open`/`rows` as well, this also ran on open and stomped the highlight that
  // openMenu had just put on the CURRENT value, so opening the picker and pressing Enter
  // committed whatever sat at row 0 (Roboto) over the font the workspace already had.
  const lastQuery = useRef(query);
  useEffect(() => {
    if (!open || !searchable) return;
    if (lastQuery.current === query) return;
    lastQuery.current = query;
    setActiveIndex(rows.findIndex((r) => !r.disabled));
  }, [open, query, rows, searchable]);

  // Type-ahead: jump to the option starting with what was typed, like a native select.
  const typed = useRef({ buffer: '', at: 0 });
  const typeAhead = useCallback(
    (ch: string) => {
      const now = Date.now();
      typed.current.buffer = now - typed.current.at > 700 ? ch : typed.current.buffer + ch;
      typed.current.at = now;
      const q = typed.current.buffer.toLowerCase();
      const start = open ? activeIndex : selectedIndex;
      const ordered = [
        ...options.slice(start + 1),
        ...options.slice(0, Math.max(start + 1, 0)),
      ];
      const hit = ordered.find((o) => !o.disabled && o.text.toLowerCase().startsWith(q));
      if (!hit) return;
      const idx = options.indexOf(hit);
      if (open) setActiveIndex(idx);
      else commit(hit.value);
    },
    [activeIndex, commit, open, options, selectedIndex],
  );

  const onTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!open) {
          openMenu();
        } else if (e.key === 'Enter' || e.key === ' ') {
          // Guard by reading ONCE: options can shrink while the list is open, and the old check
          // used `?.` on one access then dereferenced the very same index unguarded on the next.
          const active = activeIndex >= 0 ? rows[activeIndex] : undefined;
          if (active && !active.disabled) commit(active.value);
          setOpen(false);
        } else {
          setActiveIndex((i) => step(i, e.key === 'ArrowDown' ? 1 : -1));
        }
        break;
      case 'Home':
      case 'End':
        if (open) {
          e.preventDefault();
          setActiveIndex(
            e.key === 'Home' ? step(-1, 1) : step(rows.length, -1),
          );
        }
        break;
      case 'Tab':
        setOpen(false);
        break;
      default:
        if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
          e.preventDefault();
          // A searchable list has no jump-to-letter: typing opens it and seeds the box. Committing
          // on a keystroke here would also set a value the hidden <select> may not be rendering.
          if (searchable) {
            setQuery(e.key);
            openMenu(0);
          } else {
            typeAhead(e.key);
          }
        }
    }
  };

  /**
   * What the hidden <select> renders.
   *
   * `commit()` assigns `el.value = next` and the call site reads it straight back off `e.target`,
   * so the option being committed MUST exist in this same render — otherwise the assignment is
   * rejected by the DOM and every pick writes an empty string. With a filter applied that means
   * the visible rows PLUS the current value, which the filter may well have excluded.
   *
   * Unfiltered (every existing call site) this is just `options`, unchanged.
   */
  const nativeOptions = useMemo(() => {
    if (!searchable) return options;
    const out = [...rows];
    if (current && !out.some((o) => o.value === current)) {
      out.unshift(selected ?? { value: current, label: current, text: current, disabled: false });
    }
    return out;
  }, [current, options, rows, searchable, selected]);

  // A custom value is not in `options`, so `selected` is null and the trigger would fall back to
  // the placeholder — making a set field look empty. Show the raw value instead.
  const customLabel = allowCustomValue && !selected && current ? current : null;
  /**
   * Arrowing and committing while the caret is in the search box.
   *
   * The trigger's handler cannot serve here: it is not focused, and a plain character must reach
   * the input rather than being swallowed as type-ahead. Escape is deliberately absent — the
   * window-capture handler above already owns it, and owning it twice is how the surrounding modal
   * ends up closing too.
   */
  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => step(i, e.key === 'ArrowDown' ? 1 : -1));
        break;
      case 'Enter': {
        e.preventDefault();
        const active = activeIndex >= 0 ? rows[activeIndex] : undefined;
        if (active && !active.disabled) commit(active.value);
        setOpen(false);
        triggerRef.current?.focus();
        break;
      }
      case 'Home':
      case 'End':
        e.preventDefault();
        setActiveIndex(e.key === 'Home' ? step(-1, 1) : step(rows.length, -1));
        break;
      case 'Tab':
        setOpen(false);
        break;
      default:
        break;
    }
  };

  const triggerText = selected ? selected.text : (customLabel ?? placeholder ?? '');

  /**
   * The option list. Maps `rows` — the filtered, capped set — so what is drawn and what
   * `activeIndex` points at are the same array.
   */
  const list = (
    <ul
      ref={listRef}
      id={listId}
      role="listbox"
      aria-labelledby={id}
      style={
        searchable
          ? undefined
          : {
              position: 'fixed',
              left: pos?.left,
              right: pos?.right,
              top: pos?.top,
              bottom: pos?.bottom,
              // No explicit width: fixed positioning shrink-wraps to the content, bounded here.
              minWidth: pos?.minWidth,
              maxWidth: pos?.maxWidth,
              maxHeight: pos?.maxHeight,
            }
      }
      className={cn(
        'overflow-auto overscroll-contain bg-card py-1',
        searchable
          ? 'min-h-0 flex-1'
          : 'z-50 rounded-lg border border-line shadow-panel',
      )}
    >
      {rows.length === 0 && (
        <li className="px-3 py-2 text-sm text-ink-faint">
          {searchable && query.trim() ? 'No matching fonts' : 'No options'}
        </li>
      )}
      {rows.map((o, i) => {
        const isSelected = o.value === current;
        return (
          <li
            key={`${o.value}-${i}`}
            id={`${listId}-${i}`}
            data-index={i}
            role="option"
            aria-selected={isSelected}
            aria-disabled={o.disabled || undefined}
            title={o.title}
            // pointerdown (not click) so selection lands before the outside-press handler runs.
            onPointerDown={(e) => {
              // Primary button only — a right- or middle-press silently changed the value.
              if (e.button !== 0) return;
              e.preventDefault();
              if (o.disabled) return;
              commit(o.value);
              setOpen(false);
              triggerRef.current?.focus();
            }}
            onPointerEnter={() => !o.disabled && setActiveIndex(i)}
            className={cn(
              'flex cursor-pointer items-center gap-2 px-3 py-2 text-sm',
              o.disabled
                ? 'cursor-not-allowed text-ink-ghost'
                : i === activeIndex
                  ? 'bg-tint text-ink'
                  : 'text-ink',
            )}
          >
            <Check
              className={cn('h-4 w-4 shrink-0 text-action', !isSelected && 'invisible')}
              aria-hidden
            />
            {/* Wraps rather than truncating — the width cap above is what makes that finite.
                The trigger still truncates: it has to fit inside the field. */}
            <span className="break-words">{o.label}</span>
          </li>
        );
      })}
      {hiddenMore > 0 && (
        // Says what was left out rather than pretending the list ends here.
        <li className="px-3 py-2 text-xs text-ink-faint" aria-live="polite">
          {hiddenMore} more — keep typing to narrow
        </li>
      )}
    </ul>
  );

  /**
   * What actually gets portalled: the bare list, or — when searching — a panel that owns the
   * position and holds the search box above a scrolling list.
   */
  const overlay = searchable ? (
    <div
      ref={panelRef}
      style={{
        position: 'fixed',
        left: pos?.left,
        right: pos?.right,
        top: pos?.top,
        bottom: pos?.bottom,
        minWidth: pos?.minWidth,
        maxWidth: pos?.maxWidth,
        maxHeight: pos?.maxHeight,
      }}
      className="z-50 flex flex-col overflow-hidden rounded-lg border border-line bg-card shadow-panel"
    >
      <div className="shrink-0 border-b border-line-soft p-1.5">
        <input
          ref={searchRef}
          type="text"
          role="combobox"
          aria-expanded
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
          aria-label={searchPlaceholder}
          value={query}
          placeholder={searchPlaceholder}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onSearchKeyDown}
          className="focus-ring w-full rounded-md border border-line-input bg-card px-2.5 py-1.5 text-sm text-ink placeholder:text-ink-faint"
        />
      </div>
      {list}
    </div>
  ) : (
    list
  );

  return (
    <>
      {/*
        The real control, kept for form semantics: `name`, `required`, form submission and
        `e.target` all keep working. Hidden from assistive tech and the tab order — the button
        below is what users interact with — but NOT `display:none`, so browser constraint
        validation can still focus/report on it.
      */}
      <select
        ref={nativeRef}
        id={id ? `${id}-native` : undefined}
        value={current}
        onChange={() => {
          /* Changes flow through commit(); this exists only to keep React's controlled input happy. */
        }}
        disabled={disabled}
        aria-hidden
        tabIndex={-1}
        className="sr-only"
        {...forNative}
      >
        {/* An empty option guarantees the DOM value can always match `current`. */}
        {nativeOptions.every((o) => o.value !== '') && <option value="" />}
        {nativeOptions.map((o, i) => (
          // Indexed key: a filtered list can legitimately hold two rows with the same value (the
          // custom row and its match), and duplicate keys warn.
          <option key={`${o.value}-${i}`} value={o.value}>
            {o.text}
          </option>
        ))}
      </select>

      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        // While a search box is open IT holds the focus and the active-descendant wiring; leaving
        // a second one here would point assistive tech at an element the user is not in.
        aria-activedescendant={
          open && !searchable && activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined
        }
        // The id lives HERE, not on the hidden select: call sites pair this with
        // <FormField htmlFor={id}>, so the label must point at the control the user can actually
        // click and focus — and that association is what gives the button its accessible name.
        id={id}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={onTriggerKeyDown}
        {...forButton}
        className={cn(
          'focus-ring flex items-center justify-between gap-2 rounded-lg border border-line-input',
          'bg-card px-3 py-2 text-left text-sm text-ink',
          'disabled:cursor-not-allowed disabled:bg-page disabled:text-ink-faint',
          // `cn` is a plain join, NOT tailwind-merge, so a hard-coded w-full would silently beat a
          // caller's `w-20` (same specificity, source order decides). Only apply it when the
          // caller hasn't asked for a width.
          className && SETS_WIDTH.test(className) ? undefined : 'w-full',
          className,
        )}
      >
        <span className={cn('truncate', !selected && !customLabel && 'text-ink-faint')}>
          {selected ? selected.label : triggerText}
        </span>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 text-ink-faint transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </button>

      {open && pos && portalTarget && createPortal(overlay, portalTarget)}
    </>
  );
}
